import { useEffect, useRef, useCallback } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useReceiptType } from '../context/SettingsContext';
import logger from '../utils/logger';
import { clearMutedUnreadCache } from '../utils/unreadCountUtils';
import { clearFloorForRoom } from '../utils/unreadFloor';
import { isAppInForeground } from '../utils/appFocus';

/**
 * Hook to automatically send read receipts for messages in view
 * Implements TI requirements for read receipt handling
 * 
 * UPDATED: Now includes Netsfere-inspired patterns:
 * - Uses getLastVisibleEventId for deterministic last visible message
 * - Lifecycle-driven marking (mount, scroll, focus)
 * - Better debouncing logic
 * 
 * @param {string} roomId - The room ID
 * @param {Array} visibleEventIds - Array of event IDs currently visible to the user
 * @param {boolean} isActive - Whether the chat view is currently active/focused
 * @param {Function} getLastVisibleEventId - Function to get the last visible event ID (from useVisibleMessages)
 * @returns {Object} - sendReceipt function for manual receipt sending
 */
export const useReadReceipts = (roomId, visibleEventIds = [], isActive = true, getLastVisibleEventId = null) => {
  const client = useMatrixClient();
  const receiptType = useReceiptType();
  const lastReceiptSent = useRef(new Map()); // Track last receipt per room
  const pendingReceipts = useRef(new Map()); // Debounce receipts (stores timeout IDs)
  const pendingEventIds = useRef(new Map()); // Store pending event IDs per room

  // Mount guard: prevent premature receipt on initial room load.
  // Uses a timer to allow the scroll-to-marker repositioning and
  // IntersectionObserver to settle before enabling receipt sends.
  // The timer (2s) covers: useLayoutEffect scroll (~16ms) + IntersectionObserver
  // callback (~100ms) + React re-render cycle + safety margin.
  const readyToSendRef = useRef(false);
  const mountGuardTimerRef = useRef(null);

  // Clear sent receipts cache when receipt type changes
  // This ensures receipts are re-sent with the new type
  useEffect(() => {
    logger.debug('Receipt type changed, clearing sent receipts cache', { receiptType });
    lastReceiptSent.current.clear();
  }, [receiptType]);

  /**
   * Send a read receipt for a specific event
   * @param {string} eventId - Event ID to mark as read
   * @returns {Promise<void>}
   */
  const sendReceipt = useCallback(async (eventId) => {
    logger.debug('[NED-3388] sendReceipt called', { eventId, roomId });
    if (!client || !roomId || !eventId || !receiptType) {
      logger.debug('Read receipt skipped - missing requirements:', {
        hasClient: !!client,
        roomId,
        eventId,
        receiptType,
      });
      return;
    }

    try {
      const room = client.getRoom(roomId);
      if (!room) {
        logger.warn('Room not found for read receipt:', roomId);
        return;
      }

      const event = room.findEventById(eventId);
      if (!event) {
        logger.warn('Event not found for read receipt:', eventId);
        return;
      }

      // Don't send read receipts for our own messages
      const currentUserId = client.getUserId();
      const messageSender = event.getSender();
      if (messageSender === currentUserId) {
        logger.debug('Skipping read receipt for own message:', eventId);
        return;
      }

      // Check the room's current read receipt position from the server
      // This prevents sending duplicate receipts when navigating back to a room
      const lastReadEventId = room.getEventReadUpTo(currentUserId);
      
      if (lastReadEventId) {
        const lastReadEvent = room.findEventById(lastReadEventId);
        
        if (lastReadEventId === eventId) {
          logger.debug('Read receipt already exists on server for this event:', eventId);
          // Update our local tracking to match server state
          lastReceiptSent.current.set(roomId, eventId);
          return;
        }
        
        // Check if the new event is older than what's already been read on the server
        if (lastReadEvent) {
          const newEventTimestamp = event.getTs();
          const lastReadTimestamp = lastReadEvent.getTs();
          
          if (newEventTimestamp <= lastReadTimestamp) {
            logger.info('🚫 Skipping read receipt - message already read on server:', {
              eventId,
              newEventTimestamp,
              lastReadEventId,
              lastReadTimestamp,
              timeDiff: lastReadTimestamp - newEventTimestamp,
            });
            // Update our local tracking to match server state
            lastReceiptSent.current.set(roomId, lastReadEventId);
            return;
          }
        }
      }

      // Check if we already sent a receipt for this event in this session
      const lastSentEventId = lastReceiptSent.current.get(roomId);
      if (lastSentEventId === eventId) {
        logger.debug('Read receipt already sent in this session:', eventId);
        return;
      }

      // Only send receipts forward in time (don't send for older messages when scrolling up)
      // This prevents duplicate receipt sends when scrolling up through history
      if (lastSentEventId) {
        const lastSentEvent = room.findEventById(lastSentEventId);
        if (lastSentEvent) {
          const newEventTimestamp = event.getTs();
          const lastSentTimestamp = lastSentEvent.getTs();
          
          logger.debug('Comparing timestamps for receipt:', {
            eventId,
            newEventTimestamp,
            lastSentEventId,
            lastSentTimestamp,
            isOlder: newEventTimestamp < lastSentTimestamp,
          });
          
          if (newEventTimestamp < lastSentTimestamp) {
            logger.info('🚫 Skipping read receipt - message is older than last receipt sent in session:', {
              eventId,
              newEventTimestamp,
              lastSentEventId,
              lastSentTimestamp,
              timeDiff: lastSentTimestamp - newEventTimestamp,
            });
            return;
          }
        }
      }

      // Send read receipts
      //
      // Reference client behavior: always send m.read.private, conditionally send m.read
      // Both receipt types update your local unread count:
      // - Public receipts (m.read): Visible to all room members
      // - Private receipts (m.read.private): Only visible to the message sender
      //
      // The Matrix SDK updates the local unread count for BOTH types and fires
      // RoomEvent.UnreadNotifications when the count changes.

      const sendPublicReceipt = receiptType === 'm.read';

      logger.info('Sending read receipts:', {
        roomId,
        eventId,
        receiptType,
        sender: messageSender,
        sendPublicReceipt,
        sendPrivateReceipt: true, // Always send private
      });

      // Always send private receipt (m.read.private)
      await client.sendReadReceipt(event, 'm.read.private', true);

      // Conditionally send public receipt (m.read) based on setting
      if (sendPublicReceipt) {
        await client.sendReadReceipt(event, 'm.read', true);
      }

      lastReceiptSent.current.set(roomId, eventId);
      logger.info('✅ Read receipts sent successfully:', {
        roomId,
        eventId,
        sentPrivate: true,
        sentPublic: sendPublicReceipt,
      });
    } catch (error) {
      logger.error('❌ Failed to send read receipt:', error);
    }
  }, [client, roomId, receiptType]);

  /**
   * Send receipt for the most recent visible event
   * Debounced to avoid sending too many receipts
   * 
   * Uses getLastVisibleEventId if available for more accurate detection
   */
  const sendReceiptForLatestVisible = useCallback(() => {
    if (!isActive) {
      logger.debug('Read receipt skipped - chat not active');
      return;
    }

    if (!isAppInForeground()) {
      logger.debug('[useReadReceipts] Skipping receipt - app not in foreground');
      return;
    }

    if (!readyToSendRef.current) {
      logger.debug('[useReadReceipts] Skipping receipt - not ready (mount guard)');
      return;
    }

    // Try to get last visible from function if available (more accurate)
    let latestEventId = null;
    if (getLastVisibleEventId && typeof getLastVisibleEventId === 'function') {
      latestEventId = getLastVisibleEventId();
    }

    // Fallback to array if function didn't work
    if (!latestEventId && visibleEventIds && visibleEventIds.length > 0) {
      latestEventId = visibleEventIds[visibleEventIds.length - 1];
    }

    if (!latestEventId) {
      logger.debug('Read receipt skipped - no latest event ID');
      return;
    }

    // User is actively viewing this room (isActive=true check above) AND we
    // have a concrete event to mark as read. Clear any cached muted-room
    // unread count so the badge can legitimately reach 0.
    //
    // This fires ONLY from the visibleEventIds effect (not from flush-on-unmount),
    // so it won't interfere with the cache during tab switches. If sendReceipt
    // later bails (own message, duplicate), that's fine — the user has still
    // seen the messages, so the badge should clear regardless.
    clearMutedUnreadCache(roomId);

    // Also clear the persistent unread floor for this room. Active view is
    // the unambiguous "user saw these messages" signal — the Receipt /
    // m.fully_read listeners can't tell historical replay from live reads
    // after reload with persisted client state (NED-3439 Workstream A).
    const userId = client?.getUserId?.();
    if (userId) clearFloorForRoom(userId, roomId);

    logger.debug('Scheduling read receipt for latest event:', {
      roomId,
      latestEventId,
      totalVisible: visibleEventIds.length,
      source: getLastVisibleEventId ? 'function' : 'array',
    });

    // Clear any pending receipt for this room
    const pending = pendingReceipts.current.get(roomId);
    if (pending) {
      clearTimeout(pending);
      logger.debug('Cleared previous pending receipt');
    }

    // Store the pending event ID so cleanup can access it
    pendingEventIds.current.set(roomId, latestEventId);

    // Debounce receipt sending by 1 second
    const timeoutId = setTimeout(() => {
      logger.debug('[NED-3388] Debounce timer fired for', { roomId, latestEventId });
      sendReceipt(latestEventId);
      pendingReceipts.current.delete(roomId);
      pendingEventIds.current.delete(roomId);
    }, 1000);

    pendingReceipts.current.set(roomId, timeoutId);
  }, [roomId, visibleEventIds, isActive, sendReceipt, getLastVisibleEventId]);

  // Flush any pending debounced receipt for a room, then clear its tracking refs.
  // Used by both the dependency-change cleanup and the unmount cleanup.
  const flushPendingReceipt = useCallback((flushRoomId) => {
    const pending = pendingReceipts.current.get(flushRoomId);
    if (pending) {
      clearTimeout(pending);
    }
    const pendingEventId = pendingEventIds.current.get(flushRoomId);
    if (pendingEventId && client?.isLoggedIn?.()) {
      logger.debug('[NED-3388] Flushing pending receipt', { roomId: flushRoomId, pendingEventId });
      sendReceipt(pendingEventId); // fire-and-forget async
    }
    pendingReceipts.current.delete(flushRoomId);
    pendingEventIds.current.delete(flushRoomId);
  }, [client, sendReceipt]);

  // Send receipt when visible events change
  useEffect(() => {
    if (!readyToSendRef.current) {
      logger.debug('[useReadReceipts] Skipping receipt - initial mount, awaiting scroll settle');
      return;
    }

    sendReceiptForLatestVisible();

    // Cleanup: Cancel the pending timeout when visibleEventIds change mid-room.
    // The next sendReceiptForLatestVisible() call will start a fresh debounce.
    // Do NOT delete pendingEventIds here — the unmount/room-change effect needs it for flush.
    return () => {
      const pending = pendingReceipts.current.get(roomId);
      if (pending) {
        clearTimeout(pending);
        pendingReceipts.current.delete(roomId);
        // Keep pendingEventIds so the flush effect can access it on unmount
      }
    };
  }, [roomId, visibleEventIds, sendReceiptForLatestVisible]);

  // Mount guard timer: enable receipt sending after scroll-to-marker has settled.
  // This effect runs on room entry and starts a 2s timer. On room exit (cleanup),
  // it cancels any pending timer and resets the guard for the next room.
  useEffect(() => {
    readyToSendRef.current = false;
    if (mountGuardTimerRef.current) {
      clearTimeout(mountGuardTimerRef.current);
    }

    mountGuardTimerRef.current = setTimeout(() => {
      readyToSendRef.current = true;
      mountGuardTimerRef.current = null;
      logger.debug('[useReadReceipts] Mount guard timer expired, receipts enabled for', roomId);
      // Trigger a receipt send for whatever is currently visible
      sendReceiptForLatestVisible();
    }, 2000);

    return () => {
      if (mountGuardTimerRef.current) {
        clearTimeout(mountGuardTimerRef.current);
        mountGuardTimerRef.current = null;
      }
      // Reset guard in cleanup (not just setup) so it runs before the
      // visibleEventIds effect setup on room change (React runs cleanups
      // in reverse order, then setups in declaration order).
      readyToSendRef.current = false;
    };
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps -- sendReceiptForLatestVisible intentionally excluded to avoid re-triggering timer

  // Flush pending receipt when leaving room (room change or unmount).
  // This is the only place receipts are flushed — ensures the last visible message
  // gets a receipt sent before the room's hooks are torn down.
  // Also clears lastReceiptSent which the dep-change cleanup above preserves.
  // Note: readyToSendRef is reset by the mount guard timer effect (deps: [roomId]),
  // not here — this effect also fires when flushPendingReceipt changes (e.g. receipt
  // type change) and resetting readyToSendRef in that case would block receipt
  // sending until the next room switch.
  useEffect(() => {
    return () => {
      flushPendingReceipt(roomId);
      lastReceiptSent.current.delete(roomId);
    };
  }, [roomId, flushPendingReceipt]);

  // Lifecycle-driven: Mark read on window focus (Netsfere approach)
  // This ensures receipts are sent when user returns to the tab
  useEffect(() => {
    const handleFocus = () => {
      logger.debug('Window focused, triggering read receipt check');
      sendReceiptForLatestVisible();
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [sendReceiptForLatestVisible]);

  return {
    sendReceipt, // Expose for manual receipt sending if needed
    markRead: sendReceiptForLatestVisible, // Expose for manual triggering
  };
};

export default useReadReceipts;
